import { useState, useCallback, useRef } from 'react';
import type { Token } from '../types';
import type { TokenClipboard, TokenSize } from '@roll1/shared';
import { uuid, clamp } from '../utils';
import type { CanvasEngine } from './useCanvasEngine';
import { useI18n } from '../i18n';

// D&D 5e footprint in grid cells. tiny/small/medium all occupy one cell; the
// setting's tiny/small still map to 1 (sub-cell footprint isn't represented).
const TOKEN_SIZE_TO_CELLS: Record<TokenSize, number> = {
  tiny: 1, small: 1, medium: 1, large: 2, huge: 3, gargantuan: 4,
};

export interface UseTokensReturn {
  tokens: Token[];
  selectedToken: Token | null;
  selectedTokenIds: ReadonlySet<string>;
  /** Always-fresh ref to tokens array — use in render callbacks instead of `tokens` state */
  tokensRef: React.MutableRefObject<Token[]>;
  /** Always-fresh ref to selectedTokenIds — use in render callbacks */
  selectedTokenIdsRef: React.MutableRefObject<ReadonlySet<string>>;
  addToken: (opts?: Partial<Token>) => Token;
  addTokenDirect: (token: Token) => void;
  addImageToken: (imgSrc: string, name: string) => Promise<Token>;
  removeToken: (id: string) => void;
  copyToken: (id: string) => void;
  updateToken: (id: string, updates: Partial<Token>) => void;
  selectToken: (id: string | null) => void;
  selectTokens: (ids: string[]) => void;
  getTokenAt: (wx: number, wy: number, filter?: (t: Token) => boolean) => Token | null;
  moveToLayer: (id: string, direction: 'up' | 'down') => void;
  setTokens: React.Dispatch<React.SetStateAction<Token[]>>;
  /** Copy token to clipboard ref (for WS-based paste) */
  copyToClipboard: (id: string, sourcePageId: string) => void;
  /** Paste token from clipboard (creates via WS) */
  pasteFromClipboard: (send: (msg: Record<string, unknown>) => void, pageId: string | undefined) => Token | null;
  /** Clipboard ref */
  clipboardRef: React.MutableRefObject<TokenClipboard | null>;
}

export function useTokens(engine: CanvasEngine, opts?: { defaultTokenSize?: TokenSize }): UseTokensReturn {
  // Ref so addToken always reads the LIVE setting without re-creating the
  // callback (which would churn every consumer on each settings echo).
  const defaultSizeRef = useRef<TokenSize>(opts?.defaultTokenSize ?? 'medium');
  defaultSizeRef.current = opts?.defaultTokenSize ?? 'medium';
  const { t: tr } = useI18n();
  const [tokens, setTokensState] = useState<Token[]>([]);
  const [selectedToken, setSelectedToken] = useState<Token | null>(null);
  const [selectedTokenIds, setSelectedTokenIdsState] = useState<ReadonlySet<string>>(new Set());
  /** Ref mirror of tokens for synchronous reads (e.g. getTokenAt) without setState overhead */
  const tokensRef = useRef<Token[]>([]);
  const selectedTokenIdsRef = useRef<ReadonlySet<string>>(new Set());

  const setSelectedTokenIds: React.Dispatch<React.SetStateAction<ReadonlySet<string>>> = useCallback((action) => {
    setSelectedTokenIdsState(prev => {
      const next = typeof action === 'function' ? action(prev) : action;
      selectedTokenIdsRef.current = next;
      return next;
    });
  }, []);

  /** Wrapper around setTokensState that keeps tokensRef in sync */
  const setTokens: React.Dispatch<React.SetStateAction<Token[]>> = useCallback((action) => {
    setTokensState(prev => {
      const next = typeof action === 'function' ? action(prev) : action;
      tokensRef.current = next;
      return next;
    });
  }, []);

  const addToken = useCallback((opts: Partial<Token> = {}): Token => {
    const t: Token = {
      id: uuid(),
      name: opts.name || tr('tokens.defaultName'),
      x: opts.x ?? engine.mapWidth / 2,
      y: opts.y ?? engine.mapHeight / 2,
      size: opts.size || TOKEN_SIZE_TO_CELLS[defaultSizeRef.current] || 1,
      color: opts.color || '#e74c3c',
      imageSrc: opts.imageSrc || null,
      imageEl: opts.imageEl || null,
      hp: opts.hp ?? 10,
      maxHp: opts.maxHp ?? 10,
      ac: opts.ac ?? 10,
      conditions: opts.conditions || [],
      description: opts.description || '',
      notes: opts.notes || '',
      visible: opts.visible ?? true,
      characterSheetId: opts.characterSheetId,
      auras: opts.auras || [],
      vision: opts.vision,
      light: opts.light,
    };
    setTokens(prev => [...prev, t]);
    setSelectedToken(t);
    setSelectedTokenIds(new Set([t.id]));
    return t;
  }, [engine.mapWidth, engine.mapHeight, tr]);

  const addTokenDirect = useCallback((token: Token) => {
    setTokens(prev => [...prev, token]);
  }, []);

  const addImageToken = useCallback((imgSrc: string, name: string): Promise<Token> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const t = addToken({ name, imageSrc: imgSrc, imageEl: img });
        resolve(t);
      };
      img.src = imgSrc;
    });
  }, [addToken]);

  const removeToken = useCallback((id: string) => {
    setTokens(prev => prev.filter(t => t.id !== id));
    setSelectedToken(prev => prev?.id === id ? null : prev);
    setSelectedTokenIds(prev => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const copyToken = useCallback((id: string) => {
    setTokens(prev => {
      const src = prev.find(t => t.id === id);
      if (!src) return prev;
      const copy: Token = {
        ...src,
        id: uuid(),
        name: src.name + ' ' + tr('tokens.copySuffix'),
        x: src.x + engine.gridSize,
        y: src.y + engine.gridSize,
      };
      setSelectedToken(copy);
      setSelectedTokenIds(new Set([copy.id]));
      return [...prev, copy];
    });
  }, [engine.gridSize, tr]);

  const updateToken = useCallback((id: string, updates: Partial<Token>) => {
    setTokens(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
    setSelectedToken(prev => prev?.id === id ? { ...prev, ...updates } : prev);
  }, []);

  const selectToken = useCallback((id: string | null) => {
    if (!id) {
      setSelectedToken(null);
      setSelectedTokenIds(new Set());
      return;
    }
    setTokens(prev => {
      const t = prev.find(tok => tok.id === id) ?? null;
      setSelectedToken(t);
      setSelectedTokenIds(t ? new Set([t.id]) : new Set());
      return prev;
    });
  }, []);

  const selectTokens = useCallback((ids: string[]) => {
    if (ids.length === 0) {
      setSelectedToken(null);
      setSelectedTokenIds(new Set());
      return;
    }
    setTokens(prev => {
      const idSet = new Set(ids);
      const first = prev.find(tok => idSet.has(tok.id)) ?? null;
      setSelectedToken(first);
      setSelectedTokenIds(idSet);
      return prev;
    });
  }, []);

  const getTokenAt = useCallback((wx: number, wy: number, filter?: (t: Token) => boolean): Token | null => {
    // Read from ref — no React scheduler overhead
    const list = tokensRef.current;
    for (let i = list.length - 1; i >= 0; i--) {
      const t = list[i];
      if (filter && !filter(t)) continue;
      const r = (t.size * engine.gridSize) / 2;
      if (Math.abs(wx - t.x) < r && Math.abs(wy - t.y) < r) {
        return t;
      }
    }
    return null;
  }, [engine.gridSize]);

  const clipboardRef = useRef<TokenClipboard | null>(null);

  const copyToClipboard = useCallback((id: string, sourcePageId: string) => {
    const src = tokensRef.current.find(t => t.id === id);
    if (!src) return;
    const { imageEl, ...snapshot } = src;
    clipboardRef.current = { token: snapshot, sourcePageId };
  }, []);

  const pasteFromClipboard = useCallback((send: (msg: Record<string, unknown>) => void, pageId: string | undefined): Token | null => {
    const clip = clipboardRef.current;
    if (!clip) return null;
    const newId = uuid();
    const copy: Token = {
      ...clip.token,
      id: newId,
      name: clip.token.name + ' ' + tr('tokens.copySuffix'),
      x: clip.token.x + engine.gridSize,
      y: clip.token.y + engine.gridSize,
      imageEl: null,
      // 1:1 invariant — a pasted token must not share the source's sheet
      // binding; the server-side token:create guard would strip it anyway.
      characterSheetId: undefined,
    };
    addTokenDirect(copy);
    selectToken(copy.id);
    const { imageEl: _img, ...serialized } = copy;
    send({
      type: 'token:create',
      token: serialized,
      pageId,
    });
    return copy;
  }, [engine.gridSize, tr, addTokenDirect, selectToken]);

  const moveToLayer = useCallback((id: string, direction: 'up' | 'down') => {
    setTokens(prev => {
      const idx = prev.findIndex(t => t.id === id);
      if (idx === -1) return prev;
      const next = [...prev];
      if (direction === 'up' && idx < next.length - 1) {
        [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      } else if (direction === 'down' && idx > 0) {
        [next[idx], next[idx - 1]] = [next[idx - 1], next[idx]];
      }
      return next;
    });
  }, []);

  return {
    tokens,
    selectedToken,
    selectedTokenIds,
    tokensRef,
    selectedTokenIdsRef,
    addToken,
    addTokenDirect,
    addImageToken,
    removeToken,
    copyToken,
    updateToken,
    selectToken,
    selectTokens,
    getTokenAt,
    moveToLayer,
    setTokens,
    copyToClipboard,
    pasteFromClipboard,
    clipboardRef,
  };
}
